'use client';

import * as Select from '@radix-ui/react-select';
import { Check, ChevronUp } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';

type Option = {
  label: string;
  value: string;
  group?: string;
};

interface ModalSelectProps {
  services: Option[];
  onChange?: (value: string) => void;
}

function groupServices(services: Option[]) {
  const groups: { label: string | null; options: Option[] }[] = [];
  for (const service of services) {
    const key = service.group ?? null;
    const existing = groups.find((g) => g.label === key);
    if (existing) {
      existing.options.push(service);
    } else {
      groups.push({ label: key, options: [service] });
    }
  }
  return groups;
}

export function ModalSelect({ services, onChange }: ModalSelectProps) {
  const [value, setValue] = useState<string>(''); // valor controlado
  const [isMobile, setIsMobile] = useState(false);

  // Resetar valor selecionado quando a lista de serviços mudar
  useEffect(() => {
    setValue('');
  }, [services]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Atualizar valor interno e disparar callback externo
  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    onChange?.(newValue);
  };

  return (
    <Select.Root value={value} onValueChange={handleValueChange}>
      <Select.Trigger
        aria-label="Serviços"
        className="relative inline-flex w-full min-h-12 items-center justify-between rounded border border-gray-300 bg-[#F5F5F6] px-3 py-3 pr-10 text-base text-gray-600 focus:outline-none"
      >
        <Select.Value placeholder="Selecione o serviço:" />
        <Select.Icon className="absolute right-3">
          <Image
            src="/icons/arrow-down-circle.png"
            alt="Seta para baixo"
            width={18}
            height={18}
            priority={false}
          />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          position="popper"
          side={isMobile ? 'top' : 'bottom'}
          sideOffset={4}
          className="overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg z-50"
          style={{ width: 'var(--radix-select-trigger-width)' }}
        >
          <Select.ScrollUpButton className="flex items-center justify-center h-6 bg-white text-gray-700 cursor-default">
            <ChevronUp className="w-4 h-4" />
          </Select.ScrollUpButton>

          <Select.Viewport className="p-1">
            {groupServices(services).map((group, groupIndex) => (
              <Select.Group key={group.label ?? `group-${groupIndex}`}>
                {group.label && (
                  <Select.Label className="px-3 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-gray-700">
                    {group.label}
                  </Select.Label>
                )}
                {group.options.map((service) => (
                  <Select.Item
                    key={service.value + service.label}
                    value={service.value}
                    className="relative flex items-center rounded-md px-8 py-2 text-sm text-gray-900 cursor-pointer select-none focus:bg-blue-600 focus:text-white"
                  >
                    <Select.ItemText>{service.label}</Select.ItemText>
                    <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                      <Check className="w-4 h-4" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Group>
            ))}
          </Select.Viewport>

          <Select.ScrollDownButton className="flex items-center justify-center h-6 bg-white text-gray-700 cursor-default">
            <Image
              src="/icons/arrow-down-circle.png"
              alt="Scroll down"
              width={18}
              height={18}
              priority={false}
            />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
